import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon, Swatch } from "@/components/primitives";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useMyProfile } from "@/hooks/useFigData";
import type { SwatchKind } from "@/components/primitives";

type Props = {
  forceYou?: boolean;
  kind: "followers" | "following";
};

type Row = {
  id: string;
  handle: string;
  display_name: string | null;
  avatar: string;
};

export default function Connections({ forceYou = false, kind }: Props) {
  const nav = useNavigate();
  const params = useParams<{ handle?: string }>();
  const { session } = useAuth();
  const myProfile = useMyProfile(session?.user?.id);

  const [targetId, setTargetId] = useState<string | null>(null);
  const [targetHandle, setTargetHandle] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (forceYou) {
        const id = session?.user?.id ?? null;
        if (cancelled) return;
        setTargetId(id);
        setTargetHandle(myProfile?.handle ?? null);
        return;
      }
      const raw = decodeURIComponent(params.handle ?? "").replace(/^@/, "");
      if (!raw) {
        setTargetId(null);
        setTargetHandle(null);
        return;
      }
      const { data } = await (supabase as any)
        .from("profiles_public")
        .select("id, handle")
        .eq("handle", raw)
        .maybeSingle();
      if (cancelled) return;
      setTargetId(data?.id ?? null);
      setTargetHandle(data?.handle ?? raw);
    })();
    return () => { cancelled = true; };
  }, [forceYou, params.handle, session?.user?.id, myProfile?.handle]);

  useEffect(() => {
    if (!targetId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      // followers: rows where followed_id = target, look up follower_id
      // following: rows where follower_id = target, look up followed_id
      const filterColumn = kind === "followers" ? "followed_id" : "follower_id";
      const lookupColumn = kind === "followers" ? "follower_id" : "followed_id";
      const { data: rels } = await supabase
        .from("follows")
        .select(`${lookupColumn}`)
        .eq(filterColumn, targetId);
      if (cancelled) return;
      const userIds = ((rels ?? []) as Array<Record<string, string>>)
        .map((r) => r[lookupColumn])
        .filter(Boolean);
      if (userIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }
      const { data: profiles } = await (supabase as any)
        .from("profiles_public")
        .select("id, handle, display_name, avatar")
        .in("id", userIds);
      if (cancelled) return;
      setRows(((profiles ?? []) as Row[]).filter((p) => !!p.handle));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [targetId, kind]);

  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else if (forceYou) nav("/you");
    else if (targetHandle) nav(`/profile/${encodeURIComponent("@" + targetHandle)}`);
    else nav("/home");
  };

  const heading = kind === "followers" ? "Followers" : "Following";
  const subtitle = forceYou
    ? "your collectors"
    : targetHandle
      ? `@${targetHandle}`
      : "";

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100%", position: "relative" }}>
        <div style={{
          padding: "18px 20px 12px",
          borderBottom: "1px dashed hsl(var(--sand-deep))",
          display: "flex", alignItems: "center", gap: 10,
          position: "relative", zIndex: 2,
        }}>
          <button
            onClick={goBack}
            aria-label="Back"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
              width: 32, height: 32,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {Icon.chev(16, "l")}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>{subtitle}</div>
            <h1 style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26,
              color: "hsl(var(--ink))", margin: "2px 0 0", lineHeight: 1.1,
            }}>
              {heading}
            </h1>
          </div>
        </div>

        <div style={{ padding: "16px 20px 32px", position: "relative", zIndex: 2 }}>
          {loading ? (
            <div style={{
              padding: "30px 12px", textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.15em", color: "hsl(var(--ink-2))",
            }}>
              LOADING…
            </div>
          ) : rows.length === 0 ? (
            <div style={{
              padding: "40px 12px", textAlign: "center",
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 16,
              color: "hsl(var(--ink-2))", lineHeight: 1.4,
            }}>
              {kind === "followers"
                ? "No followers yet."
                : "Not following anyone yet."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => nav(`/profile/${encodeURIComponent("@" + r.handle)}`)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 12px",
                    background: "transparent",
                    border: "1px solid hsl(var(--ink-2))",
                    cursor: "pointer",
                    width: "100%", textAlign: "left",
                  }}
                >
                  <Swatch
                    kind={(r.avatar as SwatchKind) || "olive"}
                    style={{ width: 40, height: 40, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15,
                      color: "hsl(var(--ink))",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {r.display_name || `@${r.handle}`}
                    </div>
                    <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10, marginTop: 2 }}>
                      @{r.handle}
                    </div>
                  </div>
                  <span style={{ fontFamily: "monospace", color: "hsl(var(--ink-2))" }}>›</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileFrame>
  );
}
